import React, { FC, ReactNode } from 'react'
import styled from 'styled-components'
import cx from 'classnames'
import Select, { ActionMeta, Props as SelectProps } from 'react-select'

import { Option, PossibleOptionLabel, PossibleOptionValues } from '../../../lib/options'
import SvgIcon from '../Icon/SvgIcon'
import { IconName } from '../Icon/iconDef'
import { selectCss } from './styles'
import { CustomOption } from './components/CustomOption'
import { waitForElementToRender } from '../../../lib/dom'

type Props = Omit<
  SelectProps<Option<PossibleOptionValues, PossibleOptionLabel>>,
  'options' | 'onChange'
> & {
  icon?: IconName
  stopClickPropagation?: boolean
  'data-test'?: string
  options?: Option<PossibleOptionValues, PossibleOptionLabel>[]
  inputRef?: React.Ref<any>
  selectCurrentOptionOnMenuOpen?: boolean
  onChange?(option: Option | null, actionMeta: ActionMeta<Option>): void
  noOptionsMessage?: (obj: { inputValue: string }) => ReactNode
  isClearable?: boolean
  additionalDataAttributes?: {
    [key: `data-${string}`]: string
  }
}

export const FeedbotSelect: FC<Props> = ({
  icon,
  inputRef,
  className,
  stopClickPropagation = true,
  selectCurrentOptionOnMenuOpen = true,
  'data-test': dataTest,
  isClearable = false,
  additionalDataAttributes,
  ...rest
}) => {
  const currentOption = rest.value as Option
  return (
    <RelativeWrapper
      data-test={cx('feedbot-select', dataTest)}
      {...additionalDataAttributes}
      className={className}
      onClick={e => stopClickPropagation && e.stopPropagation()}
    >
      <StyledRSSelect
        {...rest}
        ref={inputRef}
        isClearable={isClearable}
        components={{
          // @ts-ignore
          Option: CustomOption,
          ...rest.components
        }}
        onMenuOpen={() =>
          !!currentOption?.value &&
          !!selectCurrentOptionOnMenuOpen &&
          waitForElementToRender(`span[data-test='option option--${currentOption.value}']`)
            .then(
              foundElement =>
                foundElement &&
                foundElement.scrollIntoView({ block: 'center', behavior: 'instant' as any })
            )
            .catch(error => console.error(error?.message))
        }
      />

      {icon && (
        <IconPosition>
          <SvgIcon name={icon} height={16} />
        </IconPosition>
      )}
    </RelativeWrapper>
  )
}

export type FeedbotSelectProps = Props

const RelativeWrapper = styled.div`
  position: relative;
  height: fit-content;
  width: 100%;
`

const IconPosition = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
`

const StyledRSSelect = styled(Select)`
  ${selectCss};
`
